import React, { Component } from 'react'
import { graphql } from 'gatsby'
import { Columns, Column, Container, Content, Hero, HeroBody, Subtitle, Title } from 'bloomer'
import styled from 'styled-components'

// import Breadcrumbs from '../../components/breadcrumbs'
import CallsToAction from '../../components/ctas'
import GetStarted from '../../components/start'
import Head from '../../components/head'
import Image from '../../components/image'
import Layout from '../../components/layout'
import Next from '../../components/next'

import walkthroughFeatures from '../../content/features/walkthrough-builder'

const FeatureHighlight = styled.div`
  align-items: center;
  display: flex;
  flex-flow: column nowrap;
  height: 25rem;
  justify-content: center;
  width: 100%;

  @media only screen and (min-width: 32rem) {
    display: block;
  }
`

const ShortHeroBody = styled(HeroBody)`
  padding-top: 3rem !important;

  @media only screen and (min-width: 45rem) {
    padding-top: 9rem !important;
  }
`

const MobileMockupImage = styled(Image)`
  margin: 0 auto;

  @media only screen and (min-width: 45rem) {
    height: 75%;
    width: 75%;
  }
`

const WebMockupImage = styled(Image)`
  margin: 0 auto;
  height: 100%;
  width: 100%;

  @media only screen and (min-width: 45rem) {
    transform: scale(1.5);
  }
`

const MockupNeighborColumn = styled(Column)`
  height: 100%;
  margin-top: 0;

  @media only screen and (min-width: 45rem) {
    margin-top: 3rem;
    padding-left: 8rem !important;
  }
`

const HeroImage = styled(Image)`
  left: 0;
  margin: 0 auto -8rem auto;
  position: absolute;
  width: 90vw;
  z-index: 1;

  @media only screen and (min-width: 45rem) {
    margin: -8rem auto -16rem auto;
    width: 75vw;
  }

  @media only screen and (min-width: 90rem) {
    left: -10%;
    margin: -8rem auto -20rem auto;
    width: 60vw;
  }

  @media only screen and (min-width: 120rem) {
    margin: -8rem auto -24rem auto;
    width: 40vw;
  }
`

const TopMarginContainer = styled(Container)`
  margin-top: 8rem !important;

  @media only screen and (min-width: 45rem) {
    margin-top: 30rem !important;
  }
`

const WalkthroughExpectationsImage = styled(Image)`
  margin: 0 auto;
`

class Walkthrough extends Component {
  render() {
    const featureHighlights = walkthroughFeatures.map((feature, i) => (
      <Column key={i} isSize={{ default: 3, mobile: 'full' }}>
        <FeatureHighlight>
          <Image alt={feature.title} file={this.props.data[feature.icon]} />
          <Subtitle isSize={4} style={{ fontWeight: 700, margin: '2rem 0' }}>
            {feature.title}
          </Subtitle>
          <Content isSize={'medium'} style={{ margin: '0 1rem' }}>
            {feature.description}
          </Content>
        </FeatureHighlight>
      </Column>
    ))

    let HeroSection = (
      <Hero isColor={'light'} isSize={'large'}>
        <ShortHeroBody>
          <Container hasTextAlign={'right'}>
            <Title hasTextAlign={'right'} isSize={1} className={'huge'}>
              Walkthrough Builder&#8482;
            </Title>
            <Subtitle hasTextAlign={'right'} isSize={3}>
              Modern Walkthrough software provided by Route.
            </Subtitle>
            <CallsToAction />
          </Container>
        </ShortHeroBody>
      </Hero>
    )

    let FeatureHighlightsSection = (
      <Hero isColor={'black'} isSize={'medium'}>
        <HeroBody>
          <TopMarginContainer hasTextAlign={'centered'}>
            <Subtitle isSize={1} style={{ margin: '1rem 0 6rem 0' }}>
              Tired of Disorganized Walkthrough notes? We make it easy.
            </Subtitle>
            <Columns hasTextAlign={'centered'} isGrid style={{ justifyContent: 'center', marginTop: '4rem' }}>
              {featureHighlights}
            </Columns>
          </TopMarginContainer>
        </HeroBody>
      </Hero>
    )

    let FeatureOne = (
      <Hero isColor={'light'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Subtitle isSize={1}>Easily Schedule, View, and Edit Walkthroughs.</Subtitle>
            <Subtitle isSize={2}>Walkthroughs on the Fly</Subtitle>
            <Columns isGrid style={{ alignItems: 'flex-start' }}>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <MobileMockupImage alt={'screen showing a schedule of walkthroughs'} file={this.props.data.schedule} />
              </Column>
              <MockupNeighborColumn isSize={{ default: 6, mobile: 'full' }}>
                <Content isSize={'medium'}>
                  You can add client info, assign team members, and set the date and time for a Walkthrough appointment
                  from your smartphone or computer. Your entire team can view the schedule and coordinate together
                  efficiently.
                </Content>
                <Content isSize={'medium'}>
                  Easily schedule, view, and edit Walkthroughs with just a few clicks. Our simple scheduling view allows
                  you to see an overview of all upcoming Walkthroughs and helps you and your team stay focused on
                  meeting new clients. Schedule building done right.
                </Content>
                <ul>
                  <li>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      CREATE CLIENTS
                    </Subtitle>
                  </li>
                  <li>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      SCHEDULE WALKTHROUGHS
                    </Subtitle>
                  </li>
                  <li>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      ASSIGN TEAM MEMBERS
                    </Subtitle>
                  </li>
                  <li>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      SHARE SCHEDULES
                    </Subtitle>
                  </li>
                </ul>
              </MockupNeighborColumn>
            </Columns>
          </Container>
        </HeroBody>
      </Hero>
    )

    let FeatureTwo = (
      <Hero isColor={'light'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Subtitle isSize={1}>Always Meet Client Expectations</Subtitle>
            <Subtitle isSize={2}>Crystal Clear Notes</Subtitle>
            <Content isSize={'large'}>
              Impress clients by capturing every detail about their site. Capture dimensions, floor types, hot points,
              and take photos for every room. All in real time.
            </Content>
            <Columns isGrid style={{ display: 'flex', flexFlow: 'row nowrap' }}>
              <Column isSize={{ mobile: '1/3' }} style={{ height: '100%' }}>
                <MobileMockupImage alt={'starting with walkthrough builder'} file={this.props.data.walkthroughStart} />
              </Column>
              <Column isSize={{ mobile: '1/3' }} style={{ height: '100%' }}>
                <MobileMockupImage
                  alt={'taking notes with walkthrough builder'}
                  file={this.props.data.walkthroughNotes}
                />
              </Column>
              <Column isSize={{ mobile: '1/3' }} style={{ height: '100%' }}>
                <MobileMockupImage
                  alt={'area details with walkthrough builder'}
                  file={this.props.data.walkthroughAreas}
                />
              </Column>
            </Columns>
            <Columns isGrid>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <Subtitle isSize={3}>Upgrade Your Walkthroughs</Subtitle>
                <Content isSize={'medium'}>
                  No more disorganized pen and paper notes. Safely save detailed notes about a client's space and cut
                  down on repeat Walkthroughs for missed information.
                </Content>
              </Column>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <Subtitle isSize={3}>Perfect Notes</Subtitle>
                <Content isSize={'medium'}>
                  Have a picture-perfect breakdown of a client's building by having photos, square footage, floor type,
                  and notes about every area and every room.
                </Content>
              </Column>
            </Columns>
            <WalkthroughExpectationsImage
              alt={`Meet Client Expectations with the Route Walkthrough Builder\u2122`}
              file={this.props.data.walkthroughExpectations}
            />
          </Container>
        </HeroBody>
      </Hero>
    )

    let FeatureThree = (
      <Hero isColor={'light'} isSize={'medium'}>
        <HeroBody>
          <Container>
            <Subtitle isSize={1}>Review and Share Smoothly</Subtitle>
            <Subtitle isSize={2}>Simple One-Click Sharing</Subtitle>
            <Content isSize={'large'}>
              Save hours on tedious work sharing your Walkthrough notes. Convert to PDF for even more sharing options.
            </Content>
            <Columns isGrid style={{ alignItems: 'center' }}>
              <Column isSize={{ default: 6, mobile: 'full' }}>
                <WebMockupImage alt={'walkthrough builder app'} file={this.props.data.walkthroughWeb} />
              </Column>
              <MockupNeighborColumn isSize={{ default: 6, mobile: 'full' }} hasTextAlign={'right'}>
                <ul>
                  <li style={{ marginBottom: '2rem' }}>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      SHARE ON THE WEB
                    </Subtitle>
                    <Content isSize={'medium'}>
                      Easily share your Walkthroughs with your team anywhere with Route's web app.
                    </Content>
                  </li>
                  <li style={{ marginBottom: '2rem' }}>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      REVIEW ORGANIZED INFO
                    </Subtitle>
                    <Content isSize={'medium'}>
                      Each Walkthrough has every area and all their notes organized neatly in a card view.
                    </Content>
                  </li>
                  <li style={{ marginBottom: '2rem' }}>
                    <Subtitle isSize={4} style={{ fontWeight: '600' }}>
                      PERFECT WALKTHROUGHS, PERFECT PROPOSALS
                    </Subtitle>
                    <Content isSize={'medium'}>
                      Use the info from your Walkthroughs to create accurate proposals and share them with your client
                      as a PDF to show that you'll provide the detailed services they need.
                    </Content>
                  </li>
                </ul>
              </MockupNeighborColumn>
            </Columns>
          </Container>
        </HeroBody>
      </Hero>
    )

    return (
      <Layout>
        <Head
          author={'Route'}
          title={`Walkthrough Builder\u2122: Perform Walkthroughs With Route`}
          banner={this.props.data.hero}
          description={`The Route Platform's Walkthrough Builder\u2122; is the ultimate sales tool for performing Walkthroughs.`}
          keywords={['Route', 'walkthrough', 'walkthrough builder', 'SaaS', 'software for service companies']}
          {...this.props}
        />
        {/* <Breadcrumbs {...this.props} /> */}
        {HeroSection}
        <HeroImage alt={`Route Walkthrough Builder\u2122 Screens`} file={this.props.data.hero} />
        {FeatureHighlightsSection}
        {FeatureOne}
        {FeatureTwo}
        {FeatureThree}
        <Next style={{ marginTop: '3.5rem' }} to={'/features/estimator'} title={'Next Up: Estimator'} />
        <GetStarted />
      </Layout>
    )
  }
}

export const query = graphql`
  query WalkthroughQuery {
    hero: file(relativePath: { eq: "assets/hero/walkthrough-builder.png" }) {
      ...SharpImage
    }
    schedule: file(relativePath: { eq: "assets/mockups/mobile/schedule-1.png" }) {
      ...SharpImage
    }
    walkthroughExpectations: file(relativePath: { eq: "assets/hero/walkthrough-meet-expectations.png" }) {
      ...SharpImage
    }
    walkthroughStart: file(relativePath: { eq: "assets/mockups/mobile/job-details-5.png" }) {
      ...SharpImage
    }
    walkthroughNotes: file(relativePath: { eq: "assets/mockups/mobile/area-details-2.png" }) {
      ...SharpImage
    }
    walkthroughAreas: file(relativePath: { eq: "assets/mockups/mobile/clocked-in-job-4.png" }) {
      ...SharpImage
    }
    walkthroughWeb: file(relativePath: { eq: "assets/mockups/web/laptop-walkthrough-details-1.png" }) {
      ...SharpImage
    }
    offlineFirst: file(relativePath: { eq: "assets/icons/flaticon/offline-first.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    mobileApp: file(relativePath: { eq: "assets/icons/flaticon/mobile-app.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    crystalClearNotes: file(relativePath: { eq: "assets/icons/flaticon/crystal-clear-notes.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    oneClickSharing: file(relativePath: { eq: "assets/icons/flaticon/one-click-sharing.png" }) {
      childImageSharp {
        fixed(width: 144, height: 144) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`

export default Walkthrough
